import { Link } from "react-router";
import { BookOpen, ChevronRight, ExternalLink, FileText } from "lucide-react";
import { RESOURCE_GUIDE_ORDER } from "../data/resourceGuides";
import { Seo } from "../components/Seo";

const caseStudies = [
  {
    title: "Dispute resolution in wealth operations",
    summary:
      "How a desk validated wire instruction screens during a client dispute, cutting external counsel review time by resolving facts internally.",
    anchor: "dispute",
  },
  {
    title: "Pharma data integrity examination",
    summary:
      "GxP-oriented visual trails for LIMS entries during an internal QA investigation, paired with existing audit logs.",
    anchor: "forensic",
  },
  {
    title: "Cross-team RCA for intermittent ERP faults",
    summary:
      "Engineering and operations aligned on a single visual reproduction of a rare posting error without sharing production credentials broadly.",
    anchor: "rca",
  },
  {
    title: "SOP adherence across outsourced processing",
    summary:
      "Policy-scoped capture verified golden-path usage during a BPO transition without recording idle desktop time.",
    anchor: "sop",
  },
];

export function Resources() {
  return (
    <>
      <Seo
        title="Resources"
        description="White papers and guides on visual session intelligence, self-hosted deployment, compliance patterns, and building an evidence-ready program."
        canonicalPath="/resources"
      />
      <div className="bg-slate-950 min-h-screen pt-28 pb-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mb-14">
          <p className="text-teal-400 text-xs uppercase tracking-widest font-semibold mb-3">Resources</p>
          <h1 className="text-slate-100 text-4xl sm:text-5xl font-extrabold tracking-tight mb-6">
            White Papers & Resources
          </h1>
          <p className="text-on-dark-muted text-lg leading-relaxed mb-4">
            Deep dives on visual session intelligence, self-hosted deployment, compliance patterns, and how to justify a
            DeskReplay-class program inside your organization. Use these with security, audit, and operations stakeholders.
          </p>
          <p className="text-on-dark-muted text-base leading-relaxed">
            PDF versions are available to qualified prospects:{" "}
            <Link to="/contact" className="text-teal-400 hover:text-teal-300 underline underline-offset-2">
              contact sales
            </Link>{" "}
            with your use case and plan tier (Pilot, PRO, or Enterprise).
          </p>
        </div>

        <section className="mb-24" id="guides">
          <div className="flex items-center gap-2 mb-10">
            <BookOpen className="text-teal-400" size={24} />
            <h2 className="text-slate-100 text-2xl font-bold">Guides & White Papers</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {RESOURCE_GUIDE_ORDER.map((g) => (
              <article
                key={g.slug}
                className="group rounded-2xl border border-slate-800 bg-slate-900/35 hover:border-teal-500/35 hover:bg-teal-500/[0.04] p-8 transition-all flex flex-col"
              >
                <div className="flex items-start justify-between gap-4 mb-4">
                  <h3 className="text-slate-100 group-hover:text-teal-300 font-bold text-xl leading-snug transition-colors">
                    {g.shortTitle}
                  </h3>
                  <ExternalLink size={20} className="text-on-dark-dim group-hover:text-teal-400 shrink-0 mt-1" />
                </div>
                <p className="text-teal-400/90 text-sm font-medium mb-3">{g.blurb}</p>
                <p className="text-on-dark-muted text-sm leading-relaxed mb-6 flex-1">{g.summary}</p>
                <Link
                  to={`/resources/${g.slug}`}
                  className="inline-flex items-center gap-1 text-teal-400 hover:text-teal-300 text-sm font-semibold"
                >
                  Read online
                  <ChevronRight size={16} />
                </Link>
              </article>
            ))}
          </div>
        </section>

        <section className="border-t border-slate-800 pt-16" id="case-studies">
          <div className="flex items-center gap-2 mb-8">
            <FileText className="text-emerald-400" size={24} />
            <h2 className="text-slate-100 text-2xl font-bold">Case study summaries</h2>
          </div>
          <p className="text-on-dark-muted mb-10 max-w-3xl leading-relaxed">
            Representative outcomes from DeskReplay deployments. Full PDFs are available on request. Use{" "}
            <Link to="/contact" className="text-teal-400 hover:text-teal-300 underline underline-offset-2">
              contact
            </Link>{" "}
            and reference the scenario name.
          </p>
          <div className="grid sm:grid-cols-2 gap-6">
            {caseStudies.map((cs) => (
              <div
                key={cs.title}
                className="rounded-2xl border border-slate-800 bg-slate-900/30 p-7 flex flex-col"
              >
                <h3 className="text-slate-200 font-semibold text-lg mb-3">{cs.title}</h3>
                <p className="text-on-dark-muted text-sm leading-relaxed flex-1 mb-5">{cs.summary}</p>
                <Link
                  to={`/solutions/${cs.anchor}`}
                  className="text-teal-400 hover:text-teal-300 text-sm font-medium inline-flex items-center gap-1"
                >
                  Related solution area
                  <ChevronRight size={14} />
                </Link>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-20 rounded-2xl border border-teal-500/20 bg-gradient-to-br from-teal-500/10 to-slate-950 p-10 text-center">
          <h2 className="text-slate-100 text-xl font-bold mb-3">Questions before you read?</h2>
          <p className="text-on-dark-muted text-sm max-w-xl mx-auto mb-6">
            Browse categorized answers on our FAQs page, or talk to sales about Enterprise bring-your-own IdP SSO and self-hosted deployment.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/faq"
              className="inline-flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-500 text-white px-6 py-3 rounded-xl text-sm font-semibold transition-all"
            >
              Open FAQs
              <ChevronRight size={16} />
            </Link>
            <Link
              to="/pricing"
              className="inline-flex items-center justify-center gap-2 border border-teal-500/35 text-teal-400 hover:bg-teal-500/10 px-6 py-3 rounded-xl text-sm font-semibold transition-colors"
            >
              Compare plans
            </Link>
          </div>
        </section>
      </div>
      </div>
    </>
  );
}
